import { TrashIcon, PencilIcon } from '@heroicons/react/24/solid';
import { Node } from '@xyflow/react';
import { useMemo, useState, useEffect } from 'react';
import Button from 'src/components/button/button';
import FormSelect from 'src/components/form-select';
import FormSwitch from 'src/components/form-switch';
import { useImportConfiguration } from 'src/context/import-configuration';
import {
  allIdsReviewed,
  getSwitchNodeIds,
  getDeviceLikeNodeIds,
} from 'src/context/import-configuration/import-review-helpers';
import NodeData, {
  NodeDataType,
} from 'src/modules/network-configuration/models/node-data';
import { NodeModel } from 'src/modules/network-configuration/models/node-model';
import StandardNodeDialogEdit from 'src/modules/network-configuration/nodes/standard-node/dialog-edit';

import { getDeviceSwitchConnectionLines } from '../utils/device-connection-helpers';
import {
  addGenericDevice,
  DEVICE_NODE_TYPES,
  removeNodeAndEdges,
  replaceDeviceNodeType,
} from '../utils/device-import-mutations';

type Mode = 'switch' | 'device';

export default function ImportStepNodesReview({ mode }: { mode: Mode }) {
  const {
    dialogState,
    updateProjectJsonData,
    importReview,
    setNodeReviewed,
    clearNodeReview,
  } = useImportConfiguration();

  const [editingId, setEditingId] = useState<string | null>(null);

  const project = dialogState?.projectJsonData;
  const nodes = project?.nodes as Node<NodeModel>[] | undefined;
  const edges = project?.edges;

  const ids = useMemo(() => {
    if (!nodes) return [];
    return mode === 'switch'
      ? getSwitchNodeIds(nodes)
      : getDeviceLikeNodeIds(nodes);
  }, [nodes, mode]);

  useEffect(() => {
    if (editingId && !nodes?.find((n) => n.id === editingId)) {
      setEditingId(null);
    }
  }, [editingId, nodes]);

  if (!project || !nodes || !edges) return null;

  const editingNode = editingId
    ? (nodes.find((n) => n.id === editingId) as Node<NodeModel> | undefined)
    : undefined;

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          {mode === 'switch'
            ? 'Confirm each switch when done.'
            : 'Change device type, add or remove devices, and see switch port connections. Confirm each device when done.'}
        </p>
        <div className="flex flex-wrap items-center justify-start gap-2">
          <Button
            type="button"
            color={
              allIdsReviewed(ids, importReview.nodeReviewed) ? 'green' : 'blue'
            }
            disabled={allIdsReviewed(ids, importReview.nodeReviewed)}
            onClick={() => {
              setNodeReviewed(ids, true);
            }}
          >
            {allIdsReviewed(ids, importReview.nodeReviewed)
              ? 'Confirmed ✓'
              : 'Confirm All'}
          </Button>

          {mode === 'device' && (
            <div>
              <Button
                type="button"
                color="teal"
                onClick={() => {
                  updateProjectJsonData((prev) => addGenericDevice(prev));
                }}
              >
                Add device
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="max-h-[min(420px,55vh)] space-y-2 overflow-y-auto pr-1">
        {ids.map((id) => {
          const node = nodes.find((n) => n.id === id);
          if (!node) return null;
          const cat = node.data.category;
          const confirmed = !!importReview.nodeReviewed[id];
          const connLines =
            mode === 'device'
              ? getDeviceSwitchConnectionLines(id, edges, nodes)
              : [];

          return (
            <div
              key={id}
              className="rounded border border-gray-200 bg-white p-3 shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-1 flex-row items-center justify-start gap-4">
                  <FormSwitch
                    id={`confirmed-${id}`}
                    name={`confirmed-${id}`}
                    wrapperClassName="w-auto"
                    checked={confirmed}
                    onChange={(value) => setNodeReviewed([id], value)}
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800">{cat.name}</p>
                    <p className="font-mono text-xs text-gray-500">{id}</p>
                    {mode === 'device' && !!connLines.length && (
                      <ul className="mt-1 list-inside list-disc text-xs text-gray-600">
                        {connLines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {mode === 'device' && (
                    <div className="min-w-[200px]">
                      <FormSelect
                        name={`device-type-${id}`}
                        label="Device type"
                        className="w-full flex-row items-center justify-center gap-2 text-sm"
                        labelClassName="whitespace-nowrap"
                        value={node.type as string}
                        options={DEVICE_NODE_TYPES.map((t) => ({
                          label: NodeData[t]?.name ?? t,
                          value: t,
                        }))}
                        onChange={(e) => {
                          const nextType = e.target.value as NodeDataType;
                          clearNodeReview([id]);
                          updateProjectJsonData((prev) =>
                            replaceDeviceNodeType(prev, id, nextType),
                          );
                        }}
                      />
                    </div>
                  )}
                  <div className="flex flex-row items-center justify-start gap-2">
                    <Button
                      type="button"
                      size="sm"
                      color="blue"
                      onClick={() => setEditingId(id)}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    {mode === 'device' && (
                      <Button
                        type="button"
                        size="sm"
                        color="red"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Remove this device and its connections?',
                            )
                          ) {
                            clearNodeReview([id]);
                            updateProjectJsonData((prev) =>
                              removeNodeAndEdges(prev, id),
                            );
                          }
                        }}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {ids.length === 0 && (
        <p className="text-sm text-gray-500">No items in this category.</p>
      )}

      {editingId && editingNode && (
        <StandardNodeDialogEdit
          id={editingId}
          open
          labelKey={String(editingNode.data.key)}
          data={editingNode.data.category}
          onClose={() => setEditingId(null)}
          onSaved={(newData, closeDialog = true) => {
            updateProjectJsonData((prev) => ({
              ...prev,
              nodes: (prev.nodes as Node<NodeModel>[]).map((n) =>
                n.id === editingId
                  ? {
                      ...n,
                      data: {
                        ...n.data,
                        category: {
                          ...n.data.category,
                          ...newData,
                        },
                        label:
                          (NodeData[n.data.key as NodeDataType]?.name ||
                            newData.name) ??
                          '',
                      },
                    }
                  : n,
              ),
              lastChanged: new Date().toISOString(),
            }));
            setNodeReviewed([editingId], true);
            if (closeDialog) {
              setEditingId(null);
            }
          }}
        />
      )}
    </div>
  );
}
